/**
 * Pure formatting for the card details page (spec #316) - kept out of the
 * screen so these decisions are cheap to test without mounting React.
 *
 * @module web/screens/cardDetailsFormat
 */
import type { CardDetailsAsset, CardDetailsJob } from "../api/types.ts";
import type { I18n } from "../i18n/index.tsx";
import { graceMinutesLeft } from "./editorFormat.ts";

/** The revision window's own hours - matches the server's `REVISION_WINDOW_HOURS` (editor-routes.ts) and the dashboard's `RevisionFace`. */
const REVISION_WINDOW_HOURS = 48;

/** "{n}h left" / "under 1h left" / "Overdue" - the big number atop the job panel. */
export function jobTimeLeftLabel(deadlineAt: string | null, t: I18n["t"], graceEndsAt?: string | null): string {
  if (!deadlineAt) return "";
  const ms = new Date(deadlineAt).getTime() - Date.now();
  if (ms <= 0) {
    const minutes = graceMinutesLeft({ deadlineAt, graceEndsAt });
    return minutes === null ? t("cardDetails.overdue") : t("cardDetails.graceLeft", { minutes });
  }
  const hours = Math.ceil(ms / 3_600_000);
  return hours < 1 ? t("cardDetails.leftUnderHour") : t("cardDetails.leftHours", { hours });
}

/** "Due Mon 09:14" / "Revision due Thu 14:10" - the job panel's due sub-line. */
export function jobDueSubLabel(job: Pick<CardDetailsJob, "deadlineAt" | "deadlineKind">, i18n: I18n): string {
  if (!job.deadlineAt) return "";
  const when = i18n.dateTime(job.deadlineAt, { weekday: "short", hour: "2-digit", minute: "2-digit" });
  return i18n.t(job.deadlineKind === "revision" ? "cardDetails.revisionDueOn" : "cardDetails.dueOn", { when });
}

/** How far through the window (accept -> deadline, or revision open -> its 48h) the clock has run, 0-100. */
export function jobProgressPercent(job: Pick<CardDetailsJob, "deadlineAt" | "deadlineKind" | "acceptedAt">): number {
  if (!job.deadlineAt) return 0;
  const end = new Date(job.deadlineAt).getTime();
  const start = job.deadlineKind === "revision" ? end - REVISION_WINDOW_HOURS * 3_600_000 : job.acceptedAt ? new Date(job.acceptedAt).getTime() : null;
  if (start === null || !(end > start)) return 0;
  return Math.max(0, Math.min(100, Math.round(((Date.now() - start) / (end - start)) * 100)));
}

/** The price tile's amount: a card still waiting for its price says so, it never shows a label with nothing next to it. */
export function priceAmountLabel(priceCents: number | null, i18n: Pick<I18n, "t" | "eur">): string {
  return priceCents != null ? i18n.eur(priceCents) : i18n.t("cardDetails.ratePending");
}

/** The price tile's sub-line: a flat static rate, the brand's own flat rate, or this editor's own locked rate. */
export function priceSubLabel(priceKind: CardDetailsJob["priceKind"], brandName: string, t: I18n["t"]): string {
  if (priceKind === "static") return t("cardDetails.rateStatic");
  if (priceKind === "brand_flat") return t("cardDetails.rateBrandFlat", { brand: brandName });
  return t("cardDetails.rateLocked");
}

/** "Hooks", "Ugc" - the deliverable spec's own free-text format field, title-cased. */
export function typeFactLabel(format: string | null): string | null {
  if (!format) return null;
  return format.charAt(0).toUpperCase() + format.slice(1);
}

/** "3 x ~3s" / "~40s" - null when the card carries no length estimate (a static card, usually). */
export function lengthFactLabel(job: Pick<CardDetailsJob, "estimatedSeconds" | "hooks">, t: I18n["t"]): string | null {
  if (job.estimatedSeconds == null) return null;
  return job.hooks && job.hooks > 1
    ? t("cardDetails.lengthHooks", { hooks: job.hooks, seconds: job.estimatedSeconds })
    : t("cardDetails.lengthSeconds", { seconds: job.estimatedSeconds });
}

/** "DE" / "EN" - the deliverable spec's own language, or the editor's own first language. */
export function languageFactLabel(language: string | null): string | null {
  return language ? language.slice(0, 2).toUpperCase() : null;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** A short icon label for an asset tile - a known domain's initials, a file's extension, or a generic fallback. */
export function assetIconText(asset: Pick<CardDetailsAsset, "kind" | "label">): string {
  if (asset.kind === "link") {
    if (/drive|doc/i.test(asset.label)) return "GD";
    if (/instagram/i.test(asset.label)) return "IG";
    if (/tiktok/i.test(asset.label)) return "TT";
    if (/youtube/i.test(asset.label)) return "YT";
    if (/facebook/i.test(asset.label)) return "FB";
    if (/trello/i.test(asset.label)) return "TR";
    return "URL";
  }
  const ext = /\.([a-zA-Z0-9]+)$/.exec(asset.label)?.[1];
  return ext ? ext.slice(0, 4).toUpperCase() : "FILE";
}
